import { ref, watch, isRef, computed } from 'vue';
import { defineStore, storeToRefs } from 'pinia';
import { useRootStore } from './root';

export const useDebugStore = defineStore('debug', () => {
  const rootStore = useRootStore();
  const { orgId, userId, isAdmin, assumingUser } = storeToRefs(rootStore);

  // Whether or not debug mode is on
  const debugModeOn = ref(false);
  const isDebugAdmin = computed(() => {
    if (isAdmin.value) return true;
    if (assumingUser.value) {
      try {
        return JSON.parse(localStorage.getItem('preAssumedUser')).qsrAdmin;
      } catch (e) {
        return false;
      }
    }
  });

  // If current user is a qsr admin, persist
  // debug mode value to local storage
  watch(debugModeOn, v => {
    if (isDebugAdmin.value) {
      localStorage.setItem('debugMode', v);
    }
  });

  // isAdmin might not be available by
  // the time we get here, so watch for
  // it ever becoming true, then check
  // local storage to see if we had turned
  // debug mode on before
  watch(isDebugAdmin, v => {
    const localDebugMode = localStorage.getItem('debugMode');
    if (v && localDebugMode) {
      debugModeOn.value = localDebugMode === "true";
    }
  }, { immediate: true });

  // The message to be displayed in the debug snackbar
  const debugNotification = ref(null);

  // The list of refs to display in the debug window
  const debugRefList = ref([
    ['orgId', orgId],
    ['userId', userId],
  ]);

  // Actions

  // Add ref to the debug window. Requires
  // passing an object with a Vue ref as its
  // only property, e.g. add({ nsn })
  function add(refObj) {
    if (typeof refObj !== 'object' || Object.keys(refObj).length !== 1 || !isRef(Object.values(refObj)[0])) {
      throw new Error('when using the add method from the debug store, you must pass an object with a Vue ref as its only property, e.g. add({ nsn })')
    }
    const entry = Object.entries(refObj)[0];
    debugRefList.value.unshift(entry);
  }

  // Display message in snackbar
  function notify(message) {
    if (!debugModeOn.value) {
      return;
    }

    debugNotification.value = message;
  }

  // Copy given text to clipboard
  async function copyToClipboard(text) {
    if (!debugModeOn.value) {
      return;
    }

    if (navigator?.clipboard) {
      try {
        await navigator.clipboard.writeText(text);
        notify(`Copied "${text}" to clipboard`);
      } catch (e) {
        notify('Could not copy to clipboard');
      }
    } else if (document.execCommand) {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.top = "0";
      textarea.style.left = "0";
      textarea.style.position = "fixed";
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
        notify(`Copied ${text} to clipboard`);
      } catch (e) {
        notify('Could not copy to clipboard');
      }
    } else {
      notify('Could not copy to clipboard');
    }
  }

  return {
    debugModeOn,
    debugNotification,
    debugRefList,
    isDebugAdmin,

    add,
    notify,
    copyToClipboard,
  };
});

